import React, { useState } from 'react';
import { format } from 'date-fns';
import { ThumbUpIcon, ThumbDownIcon } from 'features/agent-analytics/components/runCells';
import RunFeedbackControl from 'features/agent-analytics/components/RunFeedbackControl';

function formatDate(iso) {
  if (!iso) return '—';
  try { return format(new Date(iso), 'yyyy-MM-dd HH:mm'); }
  catch { return iso; }
}

function PlusGlyph({ open }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ transform: open ? 'rotate(45deg)' : 'none', transition: 'transform 120ms' }}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/**
 * User-feedback section: a collapsed "Give feedback" form (opened on demand →
 * writes via parent's onSubmit → SDK feedback endpoint) plus the history of
 * every feedback row recorded against this run.
 */
export default function RunFeedbackPanel({ run, feedback, onSubmit }) {
  const [open, setOpen] = useState(false);
  const rows = feedback || [];
  return (
    <div className="flex flex-col gap-3" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--border-default)] bg-surface px-3 py-1.5 text-[12px] font-medium text-[color:var(--text-secondary)] transition-colors hover:border-[color:var(--border-strong)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
        >
          <PlusGlyph open={open} />
          {open ? 'Close' : 'Give feedback'}
        </button>
        {open ? (
          <div className="mt-2">
            <RunFeedbackControl
              initial={{
                satisfaction: run.feedback_satisfaction,
                rating: run.feedback_rating,
                comment: run.feedback_comment,
              }}
              onSubmit={onSubmit}
            />
          </div>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <p className="text-[11px] text-[color:var(--text-muted)]">No feedback recorded yet.</p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {rows.map((fb) => {
            const pos = fb.satisfaction === 'positive';
            const neg = fb.satisfaction === 'negative';
            return (
              <li
                key={fb.feedback_id || fb.id}
                className="rounded-lg border border-[color:var(--border-subtle)] bg-surface p-2.5 text-[12px]"
              >
                <div className="flex items-center gap-2">
                  {pos ? <ThumbUpIcon size={14} filled /> : neg ? <ThumbDownIcon size={14} filled /> : null}
                  {fb.rating != null ? (
                    <span className="rounded bg-[color:var(--surface-sunken)] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-[color:var(--text-secondary)]">
                      {Number(fb.rating).toFixed(1)}
                    </span>
                  ) : null}
                  <span className="ml-auto text-[10px] tabular-nums text-[color:var(--text-muted)]">
                    {formatDate(fb.created_at || fb.timestamp)}
                  </span>
                </div>
                {fb.comment ? <p className="mt-1.5 leading-relaxed text-[color:var(--text-primary)]">{fb.comment}</p> : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
